/**
 * The near-duplicate-variant warning — D140, Calder's ask.
 *
 * @remarks
 * Calder measured every fallback/variant pair in the three shipped packs
 * (2026-09-03): of 41, two APPEND a sentence, nineteen SWAP one or two and
 * repeat the rest, twenty rewrite. 235 words were maintained in two places;
 * the checkpoint's variant repeated four of its five sentences byte-for-byte,
 * and the first edit to one side would have drifted the other with nothing
 * to notice. Those nineteen were authored in a world with only variants, so
 * they measure what the field forced — and D140's fragments are the shape
 * for "true in addition." A swap is detectable, and this detects it: a
 * variant that repeats two or more of its fallback's sentences byte-for-byte
 * is reported, with the path, as a warning (never a failure — a repeated
 * sentence is legitimate prose, and the loader cannot know the author's
 * intent, only the duplication).
 *
 * Structural, like every walk here: a conditional TEXT is any node with a
 * string `fallback` and `variants` whose entries carry `text`; conditional
 * images share the outer shape and are skipped by that last test.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition } from '@orpheus/content-schema';

import { anchorPath } from './anchored-path.js';
import { fieldOf, walkDocumentObjects } from './structural-walk.js';

/** How many repeated sentences make a variant worth naming. One is a coincidence; two is a copy. */
const REPEATED_SENTENCE_THRESHOLD = 2;

/** One variant that repeats its own fallback. */
export interface VariantRepeatsFallback {
	/**
	 * The path with its top-level index replaced by the entity's own id
	 * (`rooms[room_checkpoint].description.variants[0]`) — the identity a
	 * baseline can key on, because inserting an item above it moves the
	 * index and not the id.
	 */
	readonly anchoredPath: string;
	/** The path of the variant (`rooms[2].description.variants[0]`). */
	readonly path: string;
	/** How many of the variant's sentences appear byte-for-byte in the fallback. */
	readonly repeatedSentenceCount: number;
	/** How many sentences the variant has in all. */
	readonly sentenceCount: number;
}

/**
 * Split prose into sentences at terminal punctuation followed by whitespace —
 * the coarsest honest cut, and the one an author edits by.
 *
 * @param text - The prose
 * @returns Its sentences, trimmed, empties dropped
 */
const sentencesOf = (text: string): readonly string[] =>
	text
		.split(/(?<=[.!?])\s+/u)
		.map((sentence) => sentence.trim())
		.filter((sentence) => sentence !== '');

/**
 * Every variant in the pack that repeats two or more of its fallback's
 * sentences byte-for-byte, in document order.
 *
 * @param contentPack - The parsed pack
 * @returns The near-duplicates, with their paths
 */
export const findVariantsRepeatingFallback = (contentPack: ContentPackDefinition): readonly VariantRepeatsFallback[] => {
	const found: VariantRepeatsFallback[] = [];

	walkDocumentObjects(contentPack, (record, path) => {
		const { fallback, variants } = record;

		if (typeof fallback !== 'string' || !Array.isArray(variants)) {
			return;
		}

		const fallbackSentences = new Set(sentencesOf(fallback));

		variants.forEach((variant: unknown, variantIndex) => {
			const text = fieldOf(variant, 'text');

			if (typeof text !== 'string') {
				return;
			}

			const variantSentences = sentencesOf(text);
			const repeatedSentenceCount = variantSentences.filter((sentence) => fallbackSentences.has(sentence)).length;

			if (repeatedSentenceCount >= REPEATED_SENTENCE_THRESHOLD) {
				const variantPath = `${path}.variants[${String(variantIndex)}]`;

				found.push({
					anchoredPath: anchorPath(contentPack, variantPath),
					path: variantPath,
					repeatedSentenceCount,
					sentenceCount: variantSentences.length,
				});
			}
		});
	});

	return found;
};
